import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/legacy/build/pdf';
import { useEffect, useRef, useState } from 'react';

function PdfLoader({
    url,
    cMapUrl,
    cMapPacked,
    workerSrc = 'https://unpkg.com/pdfjs-dist@2.11.338/build/pdf.worker.min.js',
    onError,
    beforeLoad,
    errorMessage,
    children,
}) {
    const [pdfDocument, setPdfDocument] = useState(null);
    const [error, setError] = useState(null);
    const containerRef = useRef(null);
    const pdfRef = useRef(null); // holds the loaded PDFDocumentProxy
    const loadingTaskRef = useRef(null); // holds the current PDF loading task

    useEffect(() => {
        // Set the workerSrc globally if provided
        if (workerSrc) {
            GlobalWorkerOptions.workerSrc = workerSrc;
        }

        // Reset state before starting a new load
        setPdfDocument(null);
        setError(null);

        // Begin loading the PDF document
        const ownerDoc = containerRef.current ? containerRef.current.ownerDocument : undefined;
        const loadingTask = getDocument({ url, cMapUrl, cMapPacked, ownerDocument: ownerDoc });
        loadingTaskRef.current = loadingTask;
        let canceled = false;

        loadingTask.promise.then(
            (pdf) => {
                loadingTaskRef.current = null;
                if (!canceled) {
                    pdfRef.current = pdf;
                    setPdfDocument(pdf);
                } else {
                    // If the effect was canceled (component unmounted), destroy the loaded PDF to free resources
                    pdf.destroy();
                }
            },
            (err) => {
                loadingTaskRef.current = null;
                if (!canceled) {
                    setError(err);
                    if (onError) {
                        onError(err);
                    }
                }
            }
        );

        // Cleanup when the URL or options change, or on component unmount
        return () => {
            canceled = true;
            // If a PDF was loaded, destroy it to release the worker and memory
            if (pdfRef.current) {
                pdfRef.current.destroy();
                pdfRef.current = null;
            }
            // If a loading task is in progress, abort it
            if (loadingTaskRef.current) {
                loadingTaskRef.current.destroy();
                loadingTaskRef.current = null;
            }
        };
    }, [url, cMapUrl, cMapPacked, workerSrc, onError]); // re-run if URL or relevant options change
    // Render appropriate content based on loading/error state
    return (
        <div ref={containerRef}>
            {error
                ? errorMessage || null
                : !pdfDocument
                  ? beforeLoad || null
                  : typeof children === 'function'
                    ? children(pdfDocument)
                    : children}
        </div>
    );
}
export { PdfLoader };
export default PdfLoader;
